type LogLevel = "debug" | "info" | "warn" | "error" | "security";

interface LogContext {
  component?: string;
  action?: string;
  metadata?: Record<string, any>;
  [key: string]: any;
}

interface URIportsError {
  message: string;
  stack?: string;
  component?: string;
  userId?: string;
  url?: string;
  userAgent?: string;
  timestamp: string;
  level: LogLevel;
  context?: LogContext;
  securityEvent?: boolean;
  threatLevel?: "low" | "medium" | "high" | "critical";
}

class Logger {
  private isDevelopment =
    process.env.NODE_ENV === "development" || process.env.NODE_ENV === "test";
  private isProduction = process.env.NODE_ENV === "production";

  // Configurable log level with environment variable override
  // Use getter to ensure it's always current
  private get minLevel(): LogLevel {
    return this.getMinLogLevel();
  }

  private uriportsEndpoint = process.env.NEXT_PUBLIC_BUSINESS_URI_PORT
    ? `https://${process.env.NEXT_PUBLIC_BUSINESS_URI_PORT}.uriports.com/reports`
    : undefined;

  private levelPriority: Record<LogLevel, number> = {
    debug: 0,
    info: 1,
    warn: 2,
    error: 3,
    security: 4,
  };
  private recentLogTimestamps = new Map<string, number>();
  private throttleWindowMs = 5000;

  /**
   * Determine minimum log level based on environment
   */
  private getMinLogLevel(): LogLevel {
    // Allow environment variable override
    const envLogLevel = process.env.LOG_LEVEL as LogLevel;
    if (envLogLevel && this.levelPriority[envLogLevel] !== undefined) {
      return envLogLevel;
    }

    // Default levels based on environment
    if (this.isDevelopment) return "debug";
    if (this.isProduction) return "warn"; // Warnings and above in production
    return "info"; // Staging/other environments
  }

  /**
   * Check if we should log based on log level
   */
  private shouldLogWithThrottling(
    level: LogLevel,
    component?: string,
    message?: string
  ): boolean {
    // Respect the minimum log level configuration
    if (!this.shouldLog(level)) {
      return false;
    }

    // Keep errors/security fully visible.
    if (level === "error" || level === "security") {
      return true;
    }

    const key = `${level}:${component || "unknown"}:${message || ""}`;
    const now = Date.now();
    const lastSeen = this.recentLogTimestamps.get(key);
    if (lastSeen && now - lastSeen < this.throttleWindowMs) {
      return false;
    }
    this.recentLogTimestamps.set(key, now);
    return true;
  }

  // Enhanced sensitive data patterns for comprehensive sanitization
  private sensitivePatterns = [
    // API Keys and Tokens
    /sk_[a-zA-Z0-9_]+/g, // Stripe secret keys
    /pk_[a-zA-Z0-9_]+/g, // Stripe public keys
    /rk_[a-zA-Z0-9_]+/g, // Stripe restricted keys
    /whsec_[a-zA-Z0-9_]+/g, // Stripe webhook secrets
    /eyJ[a-zA-Z0-9_-]*\.[a-zA-Z0-9_-]*\.[a-zA-Z0-9_-]*/g, // JWTs
    /Bearer\s+[a-zA-Z0-9_-]+/gi, // Bearer tokens
    /[a-zA-Z0-9]{32,}/g, // Long random strings (potential tokens)

    // Database and Connection Strings
    /postgresql:\/\/[^@]+@[^\/]+/g, // PostgreSQL connection strings
    /mongodb:\/\/[^@]+@[^\/]+/g, // MongoDB connection strings
    /redis:\/\/[^@]+@[^\/]+/g, // Redis connection strings

    // Personal Information
    /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g, // Email addresses
    /\b\d{3}-\d{2}-\d{4}\b/g, // SSN format
    /\b\d{4}[\s-]?\d{4}[\s-]?\d{4}[\s-]?\d{4}\b/g, // Credit card numbers
    /\b\d{10,16}\b/g, // Long numeric sequences (potential account numbers)

    // File Paths and URLs that might contain sensitive data
    /\/api\/[^\/]+\/[^\/]+\/[^\/]+/g, // API paths with IDs
    /\/admin\/[^\/]+/g, // Admin paths
    /\/auth\/[^\/]+/g, // Auth paths

    // Wise-specific patterns
    /wise_[a-zA-Z0-9_]+/g, // Wise API keys
    /transfer_[a-zA-Z0-9_]+/g, // Transfer IDs
    /profile_[a-zA-Z0-9_]+/g, // Profile IDs
  ];

  private shouldLog(level: LogLevel): boolean {
    return this.levelPriority[level] >= this.levelPriority[this.minLevel];
  }

  private formatMessage(level: LogLevel, message: string): string {
    const timestamp = new Date().toISOString();
    return `[${timestamp}] [${level.toUpperCase()}] ${message}`;
  }

  /**
   * Enhanced data sanitization with comprehensive pattern matching
   * Always sanitizes sensitive keys, but pattern-based sanitization is skipped in development
   */
  private sanitizeData(data: any, depth: number = 0): any {
    if (depth > 10) return "[MAX_DEPTH_REACHED]"; // Prevent infinite recursion

    if (data === null || data === undefined) {
      return data;
    }

    if (typeof data === "string") {
      return this.sanitizeString(data);
    }

    if (typeof data === "number" || typeof data === "boolean") {
      return data;
    }

    if (data instanceof Error) {
      return {
        name: data.name,
        message: this.sanitizeString(data.message),
        stack: data.stack ? this.sanitizeString(data.stack) : undefined,
      };
    }

    if (Array.isArray(data)) {
      return data.map(item => this.sanitizeData(item, depth + 1));
    }

    if (typeof data === "object") {
      const sanitized: Record<string, any> = {};
      for (const [key, value] of Object.entries(data)) {
        // Always sanitize sensitive keys regardless of environment
        if (this.isSensitiveKey(key)) {
          sanitized[key] = "[REDACTED]";
          continue;
        }
        sanitized[key] = this.sanitizeData(value, depth + 1);
      }
      return sanitized;
    }

    return "[UNKNOWN_TYPE]";
  }

  /**
   * Check if a key name indicates sensitive data
   */
  private isSensitiveKey(key: string): boolean {
    const sensitiveKeys = [
      "password",
      "passwd",
      "pwd",
      "secret",
      "token",
      "key",
      "auth",
      "authorization",
      "apikey",
      "api_key",
      "private_key",
      "secret_key",
      "access_token",
      "refresh_token",
      "jwt",
      "session",
      "cookie",
      "credential",
      "credential_id",
      "credential_secret",
      "client_secret",
      "client_id",
      "consumer_key",
      "consumer_secret",
      "oauth_token",
      "oauth_secret",
      "api_secret",
      "webhook_secret",
      "signing_secret",
      "encryption_key",
      "decryption_key",
      "master_key",
      "admin_key",
      "root_key",
      "superuser_key",
      "database_url",
      "connection_string",
      "dsn",
      "uri",
      "url",
      "endpoint",
      "email",
      "phone",
      "ssn",
      "social_security",
      "credit_card",
      "card_number",
      "account_number",
      "routing_number",
      "iban",
      "swift",
      "bic",
      "tax_id",
      "ein",
      "vat",
      "gst",
      "pan",
      "aadhar",
      "passport",
      "license",
      "id_number",
      "wise_token",
      "wise_key",
      "stripe_key",
      "stripe_secret",
      "transfer_id",
      "profile_id",
      "recipient_id",
      "quote_id",
      "order_id",
      "ticket_id",
    ];

    const lowerKey = key.toLowerCase();
    return sensitiveKeys.some(sensitive => lowerKey.includes(sensitive));
  }

  /**
   * Sanitize string data by removing sensitive patterns
   * Always redacts JWT tokens and other critical patterns, even in development
   */
  private sanitizeString(str: string): string {
    if (!str || typeof str !== "string") return str;

    let sanitized = str;

    // Always redact JWT tokens regardless of environment
    const jwtPattern = /eyJ[a-zA-Z0-9_-]*\.[a-zA-Z0-9_-]*\.[a-zA-Z0-9_-]*/g;
    sanitized = sanitized.replace(jwtPattern, "[REDACTED]");

    // Skip other pattern-based sanitization in development mode
    if (this.isDevelopment) {
      // Limit string length to prevent log flooding
      if (sanitized.length > 1000) {
        sanitized = sanitized.substring(0, 1000) + "... [TRUNCATED]";
      }
      return sanitized;
    }

    // Apply all other sensitive patterns in non-development
    for (const pattern of this.sensitivePatterns) {
      // Skip JWT pattern as we already handled it above
      if (pattern === jwtPattern) continue;
      sanitized = sanitized.replace(pattern, "[REDACTED]");
    }

    // Additional sanitization for URLs and paths
    sanitized = sanitized
      .replace(/\/api\/[^\/]+\/[^\/]+\/[^\/]+/g, "/api/[REDACTED]")
      .replace(/\/admin\/[^\/]+/g, "/admin/[REDACTED]")
      .replace(/\/auth\/[^\/]+/g, "/auth/[REDACTED]")
      .replace(/\/ticket\/[^\/]+/g, "/ticket/[REDACTED]")
      .replace(/\/order\/[^\/]+/g, "/order/[REDACTED]");

    // Limit string length to prevent log flooding
    if (sanitized.length > 1000) {
      sanitized = sanitized.substring(0, 1000) + "... [TRUNCATED]";
    }

    return sanitized;
  }

  /**
   * Send error reports to URIports for centralized error tracking
   */
  private async sendToURIports(
    message: string,
    level: LogLevel,
    context?: LogContext,
    error?: Error
  ): Promise<void> {
    // Only send errors, warnings, and security events to URIports in production
    if (
      !this.uriportsEndpoint ||
      this.isDevelopment ||
      (level !== "error" && level !== "warn" && level !== "security")
    ) {
      return;
    }

    try {
      const errorReport: URIportsError = {
        message: this.sanitizeString(message),
        stack: error?.stack ? this.sanitizeString(error.stack) : undefined,
        component: context?.component,
        userId: context?.metadata?.userId ? "[REDACTED]" : undefined,
        url:
          typeof window !== "undefined"
            ? this.sanitizeString(window.location.href)
            : undefined,
        userAgent:
          typeof window !== "undefined"
            ? this.sanitizeString(navigator.userAgent)
            : undefined,
        timestamp: new Date().toISOString(),
        level,
        context: this.sanitizeData(context),
        securityEvent: level === "security",
        threatLevel: this.assessThreatLevel(message, context),
      };

      // Send to URIports endpoint
      await fetch(this.uriportsEndpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(errorReport),
      });
    } catch (reportError) {
      // Silently fail URIports reporting to avoid infinite loops
      // Don't use console.error - it causes console errors in Lighthouse
      // Errors are already logged through the normal logger flow
      // Only log in development for debugging purposes
      if (this.isDevelopment && typeof console !== "undefined") {
        console.log("Failed to send error to URIports:", reportError);
      }
    }
  }

  /**
   * Assess threat level for security events
   */
  private assessThreatLevel(
    message: string,
    _context?: LogContext
  ): "low" | "medium" | "high" | "critical" {
    const lowerMessage = message.toLowerCase();

    // Critical threats
    if (
      lowerMessage.includes("sql injection") ||
      lowerMessage.includes("xss") ||
      lowerMessage.includes("csrf") ||
      lowerMessage.includes("authentication bypass") ||
      lowerMessage.includes("privilege escalation")
    ) {
      return "critical";
    }

    // High threats
    if (
      lowerMessage.includes("unauthorized access") ||
      lowerMessage.includes("invalid token") ||
      lowerMessage.includes("rate limit exceeded") ||
      lowerMessage.includes("suspicious activity")
    ) {
      return "high";
    }

    // Medium threats
    if (
      lowerMessage.includes("validation failed") ||
      lowerMessage.includes("invalid input") ||
      lowerMessage.includes("malformed request")
    ) {
      return "medium";
    }

    return "low";
  }

  debug(message: string, context?: LogContext, ...args: any[]): void {
    // Don't log during tests to reduce noise
    if (process.env.NODE_ENV === "test") {
      return;
    }

    if (this.shouldLogWithThrottling("debug", context?.component, message)) {
      const sanitizedContext = this.sanitizeData(context);
      const sanitizedArgs = args.map(arg => this.sanitizeData(arg));
      console.debug(
        this.formatMessage("debug", message),
        sanitizedContext,
        ...sanitizedArgs
      );
    }
  }

  info(message: string, context?: LogContext, ...args: any[]): void {
    // Don't log during tests to reduce noise
    if (process.env.NODE_ENV === "test") {
      return;
    }

    if (this.shouldLogWithThrottling("info", context?.component, message)) {
      const sanitizedContext = this.sanitizeData(context);
      const sanitizedArgs = args.map(arg => this.sanitizeData(arg));
      console.log(
        this.formatMessage("info", message),
        sanitizedContext,
        ...sanitizedArgs
      );
    }
  }

  warn(message: string, context?: LogContext, ...args: any[]): void {
    // Don't log during tests to reduce noise
    if (process.env.NODE_ENV === "test") {
      return;
    }

    if (this.shouldLogWithThrottling("warn", context?.component, message)) {
      const sanitizedContext = this.sanitizeData(context);
      const sanitizedArgs = args.map(arg => this.sanitizeData(arg));
      console.warn(
        this.formatMessage("warn", message),
        sanitizedContext,
        ...sanitizedArgs
      );

      // Send warning to URIports
      this.sendToURIports(message, "warn", context);
    }
  }

  error(
    message: string,
    context?: LogContext,
    error?: Error,
    ...args: any[]
  ): void {
    // Don't log errors during tests to reduce noise from intentional test error scenarios
    if (process.env.NODE_ENV === "test") {
      return;
    }

    // Always log errors (no throttling for critical issues)
    if (this.shouldLog("error")) {
      const sanitizedContext = this.sanitizeData(context);
      const sanitizedArgs = args.map(arg => this.sanitizeData(arg));
      const errorArgs = error
        ? [this.sanitizeData(error), ...sanitizedArgs]
        : sanitizedArgs;
      console.error(
        this.formatMessage("error", message),
        sanitizedContext,
        ...errorArgs
      );

      // Send error to URIports
      this.sendToURIports(message, "error", context, error);
    }
  }

  /**
   * Specialized method for security events
   */
  security(
    message: string,
    context?: LogContext,
    threatLevel: "low" | "medium" | "high" | "critical" = "medium",
    ...args: any[]
  ): void {
    // Always log security events (no throttling for security issues)
    if (this.shouldLog("security")) {
      const sanitizedContext = this.sanitizeData(context);
      const sanitizedArgs = args.map(arg => this.sanitizeData(arg));

      // Enhanced security logging
      const securityContext = {
        ...sanitizedContext,
        threatLevel,
        timestamp: new Date().toISOString(),
        securityEvent: true,
      };

      console.error(
        this.formatMessage(
          "security",
          `[${threatLevel.toUpperCase()}] ${message}`
        ),
        securityContext,
        ...sanitizedArgs
      );

      // Always send security events to URIports
      this.sendToURIports(message, "security", securityContext);
    }
  }

  /**
   * Specialized method for capturing exceptions with URIports integration
   */
  captureException(
    error: Error,
    context?: LogContext & { component?: string; userId?: string }
  ): void {
    const message = `Uncaught exception: ${error.message}`;
    this.error(message, context, error);
  }

  /**
   * Log security events with threat assessment
   */
  logSecurityEvent(
    event: string,
    details: Record<string, any>,
    threatLevel: "low" | "medium" | "high" | "critical" = "medium"
  ): void {
    this.security(
      event,
      {
        component: "SecurityMonitor",
        action: "security_event",
        metadata: details,
      },
      threatLevel
    );
  }

  /**
   * Log authentication events
   */
  logAuthEvent(
    action:
      | "login"
      | "logout"
      | "failed_login"
      | "password_reset"
      | "token_refresh",
    userId?: string,
    metadata?: Record<string, any>
  ): void {
    const threatLevel = action === "failed_login" ? "medium" : "low";
    // Sanitize metadata first, then ensure userId is redacted
    const sanitizedMetadata = metadata ? this.sanitizeData(metadata) : {};
    this.security(
      `Authentication event: ${action}`,
      {
        component: "Auth",
        action,
        metadata: {
          ...sanitizedMetadata,
          userId: userId ? "[REDACTED]" : "anonymous",
        },
      },
      threatLevel
    );
  }

  /**
   * Log API access events
   */
  logApiAccess(
    endpoint: string,
    method: string,
    status: number,
    userId?: string,
    metadata?: Record<string, any>
  ): void {
    const threatLevel = status >= 400 ? "medium" : "low";
    // Sanitize metadata first, then ensure endpoint and userId are redacted
    const sanitizedMetadata = metadata ? this.sanitizeData(metadata) : {};
    this.security(
      `API access: ${method} ${endpoint}`,
      {
        component: "API",
        action: "api_access",
        metadata: {
          ...sanitizedMetadata,
          endpoint: "[REDACTED]",
          method,
          status,
          userId: userId ? "[REDACTED]" : "anonymous",
        },
      },
      threatLevel
    );
  }

  private sanitizeContext(context?: LogContext): LogContext {
    if (!context) return {};
    return this.sanitizeData(context);
  }

  // Specialized methods for common use cases
  api(endpoint: string, method: string, status?: number, data?: any): void {
    // Log API errors (500+ status codes) as errors
    if (status && status >= 500) {
      this.error(`API ${method} ${endpoint} failed with status ${status}`, {
        component: "API",
        metadata: {
          status,
          hasData: !!data,
          endpoint: this.sanitizeString(endpoint),
        },
      });
      return;
    }

    // Log other API calls as debug
    this.debug(`API ${method} ${endpoint}`, {
      component: "API",
      metadata: {
        status,
        hasData: !!data,
        endpoint: this.sanitizeString(endpoint),
      },
    });
  }

  user(action: string, userId?: string, metadata?: Record<string, any>): void {
    // Log user actions as info
    this.info(`User action: ${action}`, {
      component: "USER",
      metadata: {
        userId: userId ? (this.isDevelopment ? userId : "[REDACTED]") : "none",
        ...this.sanitizeData(metadata),
      },
    });
  }
}

export const logger = new Logger();

// Utility function to properly convert any value to an Error object
export function toError(error: any): Error {
  // Handle null/undefined
  if (error == null) {
    return new Error("Unknown error");
  }

  // Handle Error instances
  if (error instanceof Error) {
    return error;
  }

  // Handle objects
  if (typeof error === "object") {
    try {
      // Try to extract meaningful information from the object
      let message = "Unknown error";

      if (typeof error.message === "string") {
        message = error.message;
      } else if (typeof error.error === "string") {
        message = error.error;
      } else if (typeof error.details === "string") {
        message = error.details;
      } else if (typeof error.toString === "function") {
        const stringified = error.toString();
        if (stringified !== "[object Object]") {
          message = stringified;
        }
      }

      const errorObj = new Error(message);

      // Preserve additional properties if they exist
      if (error.code && typeof error.code === "string") {
        errorObj.name = error.code;
      }
      if (error.stack && typeof error.stack === "string") {
        errorObj.stack = error.stack;
      }

      return errorObj;
    } catch {
      // If JSON.stringify fails or there's any other issue, fall back to basic conversion
      return new Error("Error object could not be processed");
    }
  }

  // Handle primitives
  return new Error(String(error));
}

// Utility function to sanitize sensitive data before logging
// Always sanitizes sensitive keys regardless of environment
export function sanitizeForLog(data: any): any {
  if (typeof data !== "object" || data === null) {
    return data;
  }

  // Don't sanitize Error objects
  if (data instanceof Error) {
    return data;
  }

  const sensitiveKeys = [
    "password",
    "token",
    "secret",
    "key",
    "authorization",
    "apikey",
    "api_key",
    "email",
    "phone",
    "ssn",
    "credit_card",
    "account_number",
    "wise_token",
    "stripe_key",
    "transfer_id",
    "profile_id",
  ];
  const sanitized = Array.isArray(data) ? [...data] : { ...data };

  for (const key in sanitized) {
    if (
      sensitiveKeys.some(sensitive => key.toLowerCase().includes(sensitive))
    ) {
      sanitized[key] = "[REDACTED]";
    } else if (typeof sanitized[key] === "object" && sanitized[key] !== null) {
      sanitized[key] = sanitizeForLog(sanitized[key]);
    }
  }

  return sanitized;
}
